import axios from 'axios';
import { getApiBaseUrl } from './api';

export type SetupStep = 'welcome' | 'about' | 'apps' | 'permissions' | 'ideas' | 'connect' | 'start';
export type Decision = 'yes' | 'approval' | 'no';
export type IdeaKind = 'skill' | 'plugin' | 'build';
export type IdeaFit = 'fits' | 'change' | 'scoping';

export interface SetupApp { slug: string; label: string; logo?: string }

export interface SetupIdea {
  id: string;
  title: string;
  summary: string;
  kind: IdeaKind;
  apps: string[];
  fit: IdeaFit;
  fit_note: string;
}

export interface SetupRow {
  step: SetupStep;
  skipped: SetupStep[];
  about: { job_title?: string; department?: string; work?: string };
  apps: SetupApp[];
  ideas: SetupIdea[];
  picked: string[];
  finished_at: string | null;
}

export interface SetupView {
  applies: boolean;
  active: boolean;
  started: boolean;
  role: string;
  email: string;
  company_name: string;
  name: string;
  phone: string;
  job_title: string;
  department: string;
  department_locked: boolean;
  may_create_department: boolean;
  departments: string[];
  owner_problem: boolean;
  setup: SetupRow | null;
}

export interface CatalogGroup { id: string; label: string; apps: SetupApp[] }

export interface PermissionAction { key: string; label: string; risk: string; decision: Decision | null }
export interface PermissionGroup {
  key: 'view' | 'change' | 'remove';
  label: string;
  locked: boolean;
  decision: Decision | 'mixed' | null;
  actions: PermissionAction[];
}
export interface AppPermissions { tool: string; groups: PermissionGroup[]; total_available: number }

export interface SetupHandoff { id: string; path: string }

const base = () => `${getApiBaseUrl()}/aai/setup`;

export const setupApi = {
  get: () => axios.get<SetupView>(base()),
  begin: () => axios.post<SetupView>(`${base()}/begin`, {}),
  step: (step: SetupStep, skipped?: SetupStep) => axios.post<SetupView>(`${base()}/step`, { step, skipped }),
  welcome: (body: { name?: string; phone?: string }) => axios.post<SetupView>(`${base()}/welcome`, body),
  about: (body: { job_title: string; department: string; work: string }) => axios.post<SetupView>(`${base()}/about`, body),
  catalog: () => axios.get<{ groups: CatalogGroup[] }>(`${base()}/apps/catalog`),
  apps: (apps: SetupApp[]) => axios.post<SetupView>(`${base()}/apps`, { apps }),
  permissions: (tool: string) => axios.get<AppPermissions>(`${base()}/permissions`, { params: { tool } }),
  setPermissions: (tool: string, decisions: Record<string, Decision>) =>
    axios.post<AppPermissions>(`${base()}/permissions`, { tool, decisions }),
  safePermissions: () => axios.post<SetupView>(`${base()}/permissions/safe`, {}),
  findIdeas: () => axios.post<SetupView>(`${base()}/ideas`, {}),
  myIdeas: () => axios.get<{ ideas: SetupIdea[]; picked: string[] }>(`${base()}/ideas`),
  pick: (ids: string[]) => axios.post<SetupView>(`${base()}/pick`, { ids }),
  handoffIdeas: (ids: string[] = []) => axios.post<SetupHandoff>(`${base()}/handoff/ideas`, { ids }),
  handoffProblem: (body: { statement: string; today: string; after: string; metric: string }) =>
    axios.post<SetupHandoff>(`${base()}/handoff/problem`, body),
  finish: () => axios.post<SetupView>(`${base()}/finish`, {}),
};

export const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};
